/**
 * Project page extras (spec 2026-10-14-workspaces): the richer project
 * detail, meetings, recurring jobs + their drafts, message roles and people
 * tags. Contract spellings from the spec's "API contracts"; every list a
 * server may omit defaults to empty in `withDefaults`.
 */
import { request } from '../api.js';
import type { MessageRow, Project, ProjectDetail } from '../lifeos/types.js';

const enc = encodeURIComponent;
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export type MeetingRecurrence = 'none' | 'weekly' | 'monthly';

export interface ProjectMeeting {
  id: string;
  title: string;
  startsAt: string;
  endsAt?: string | null;
  durationMin?: number | null;
  recurrence?: string | null;
  location?: string | null;
}

export interface ProjectJob {
  id: string;
  projectId: string;
  kind: string;
  title: string;
  /** Cron in America/New_York. */
  schedule: string;
  enabled: boolean;
  lastRunAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  nextRunAt: string | null;
  /** On a parent project's page: the sub-project this job belongs to (null = this project). */
  projectName?: string | null;
}

export type DraftStatus = 'ready' | 'sent' | 'dismissed';

export interface ProjectDraft {
  id: string;
  projectId: string;
  jobId: string | null;
  kind: string;
  title: string;
  body: string;
  status: DraftStatus;
  createdAt: string;
  /** On a parent project's page: the sub-project this draft belongs to (null = this project). */
  projectName?: string | null;
}

export interface ProjectContact {
  id: number;
  name: string;
  contactName?: string | null;
  tags?: string[];
  role?: string | null;
}

/** The new compact timeline row (older servers send full TimelineItems instead). */
export interface ProjectTimelineEntry {
  at: string;
  kind: string;
  title: string | null;
}

export type ProjectX = Project & { targetDate?: string | null; priority?: number | null };

export interface ProjectDetailX extends Omit<ProjectDetail, 'project' | 'timeline'> {
  project: ProjectX;
  timeline: (ProjectDetail['timeline'][number] | ProjectTimelineEntry)[];
  meetings: ProjectMeeting[];
  toReply: MessageRow[];
  contacts: ProjectContact[];
  jobs: ProjectJob[];
  drafts: ProjectDraft[];
}

/** Fill every list a server may leave out, so a mid-deploy server never crashes the page. */
export function withDefaults(d: Partial<ProjectDetailX> & { project: ProjectX }): ProjectDetailX {
  return {
    project: d.project,
    parent: d.parent ?? null,
    children: d.children ?? [],
    tasks: d.tasks ?? [],
    runs: d.runs ?? [],
    timeline: d.timeline ?? [],
    files: d.files ?? [],
    notes: d.notes ?? [],
    messages: d.messages ?? [],
    meetings: d.meetings ?? [],
    toReply: d.toReply ?? [],
    contacts: d.contacts ?? [],
    jobs: d.jobs ?? [],
    drafts: d.drafts ?? [],
  };
}

export async function getProjectX(id: string): Promise<ProjectDetailX> {
  return withDefaults(await request<Partial<ProjectDetailX> & { project: ProjectX }>(`/projects/${enc(id)}`));
}

export function updateProjectX(
  id: string,
  patch: { targetDate?: string | null; priority?: number } & Record<string, unknown>,
): Promise<unknown> {
  return request(`/projects/${enc(id)}`, json('PUT', patch));
}

// --- Meetings ----------------------------------------------------------------------------

export interface MeetingInput {
  title: string;
  startsAt: string;
  durationMin: number;
  recurrence: MeetingRecurrence;
  location?: string;
}

export function addMeeting(projectId: string, body: MeetingInput): Promise<{ event: ProjectMeeting }> {
  return request(`/projects/${enc(projectId)}/meetings`, json('POST', body));
}
export function deleteMeeting(projectId: string, eventId: string): Promise<unknown> {
  return request(`/projects/${enc(projectId)}/meetings/${enc(eventId)}`, { method: 'DELETE' });
}

// --- Jobs + drafts ------------------------------------------------------------------------

export function updateJob(projectId: string, jobId: string, patch: Partial<Pick<ProjectJob, 'enabled' | 'title' | 'schedule'>>): Promise<unknown> {
  return request(`/projects/${enc(projectId)}/jobs/${enc(jobId)}`, json('PUT', patch));
}
export function runJob(projectId: string, jobId: string): Promise<{ draft: ProjectDraft | null }> {
  return request(`/projects/${enc(projectId)}/jobs/${enc(jobId)}/run`, json('POST', {}));
}
export function updateDraft(id: string, patch: { status?: DraftStatus; body?: string; title?: string }): Promise<unknown> {
  return request(`/drafts/${enc(id)}`, json('PUT', patch));
}

// --- Message roles + people tags ---------------------------------------------------------

export type RoleBy = 'model' | 'owner' | 'rule' | null;
export type MessageWithRole = MessageRow & { roleBy?: RoleBy };

/** The owner's one-tap flip: personal or a project's work. Recorded server-side as a correction. */
export function setMessageRole(id: number, body: { sphereKey: string; projectId: string | null }): Promise<unknown> {
  return request(`/messages/${id}/role`, json('PUT', body));
}

export function setPersonTags(id: number, tags: string[]): Promise<unknown> {
  return request(`/people/${id}`, json('PUT', { tags }));
}

export const STOCK_TAGS = ['friend', 'client', 'family', 'colleague', 'vendor'] as const;

/** Lowercase, trimmed, unique; empties dropped. */
export function normTags(tags: readonly string[]): string[] {
  const out: string[] = [];
  for (const t of tags) {
    const v = t.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 40);
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

// --- Wording -------------------------------------------------------------------------------

const DOW = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

function hourWord(h: number, m: number): string {
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ap}` : `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}

function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${s}`;
}

/** '0 18 * * 0' → 'Sundays 6 PM'; '30 9 * * 1-5' → 'Weekdays 9:30 AM'; unknown shapes come back as-is. */
export function cronInWords(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [mi, hr, dom, mon, dow] = parts as [string, string, string, string, string];
  if (!/^\d+$/.test(mi) || !/^\d+$/.test(hr) || mon !== '*') return cron;
  const at = hourWord(Number(hr), Number(mi));
  if (dom === '*' && dow === '*') return `Daily ${at}`;
  if (dom === '*') {
    if (dow === '1-5') return `Weekdays ${at}`;
    if (dow === '0,6' || dow === '6,0') return `Weekends ${at}`;
    const days = dow.split(',').map((d) => Number(d) % 7);
    if (days.every((d) => Number.isInteger(d) && d >= 0 && d <= 6)) {
      const names = days.map((d) => DOW[d]!);
      return `${names.length > 1 ? names.map((n) => n.slice(0, 3)).join(', ') : names[0]} ${at}`;
    }
    return cron;
  }
  if (dow === '*' && /^\d+$/.test(dom)) return `Monthly on the ${ordinal(Number(dom))}, ${at}`;
  return cron;
}

/** 'Oct 30' plus days left: 'Due Oct 30 · 16d', 'Due Oct 1 · 3d late', 'Due today'. */
export function targetLabel(targetDate: string, today: string, shortDate: (ymd: string, today?: string) => string, days: number): string {
  if (days === 0) return 'Due today';
  return `Due ${shortDate(targetDate, today)} · ${days < 0 ? `${-days}d late` : `${days}d`}`;
}
